/**
 * MachineDefaultTestsDrawer.tsx
 * Right-side drawer for the Machines page (REQ-06, round 2): lists the tests a
 * machine SUPPORTS and lets an admin set/clear this machine as the per-test
 * default (TestCatalogItem.DefaultMachineKey — NOT MachineTaskCapability.IsDefault).
 *
 * Data:
 *   - GET /api/machines/:key/supported-tests → fetchMachineSupportedTests()
 *     (the test list + CurrentlyDefault for THIS machine)
 *   - getTestCatalogItems() → cross-reference to learn whether a test that is not
 *     default HERE is default ELSEWHERE (so we can show a muted "Default set
 *     elsewhere" chip and confirm re-pointing).
 *
 * Writes:
 *   - Toggle ON  → updateTestCatalogItem(testKey, { DefaultMachineKey: machineKey })
 *   - Toggle OFF → updateTestCatalogItem(testKey, { DefaultMachineKey: null })
 *   Re-pointing a test that currently defaults to a DIFFERENT machine first asks
 *   for confirmation via the shared ConfirmDialog (owned by the parent).
 *
 * Modeled on BookingDetailDrawer.tsx (header / scrollable body / footer skeleton).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Button,
  Stack,
  Switch,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import {
  fetchMachineSupportedTests,
  type MachineRow,
  type MachineSupportedTest,
} from '../../../services/scheduleMachines';
import {
  getTestCatalogItems,
  updateTestCatalogItem,
} from '../../../services/testCatalogService';
import type { ConfirmDialogState } from '../../../components/shared/ConfirmDialog';

/** Lower-cased GUID compare helper (SQL Server upper-cases uniqueidentifier). */
const eq = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a ?? '').toLowerCase() === (b ?? '').toLowerCase();

interface MachineDefaultTestsDrawerProps {
  open: boolean;
  /** The machine whose supported tests are shown. Null renders an empty drawer. */
  machine: MachineRow | null;
  /**
   * All machines (already loaded on the parent page). Used to resolve a test's
   * current DefaultMachineKey to a human label ("Code") for the "default
   * elsewhere" chip and the re-point confirmation message.
   */
  machines: MachineRow[];
  onClose: () => void;
  /**
   * Opens the parent-owned ConfirmDialog (re-pointing a test that defaults to a
   * different machine). The drawer supplies the state; the parent renders it.
   */
  onConfirm: (state: ConfirmDialogState) => void;
  /** Toast feedback after a write (success or failure). */
  onToast: (message: string, severity: 'success' | 'error') => void;
}

/** Internal row model: supported-test info + where its current default points. */
interface TestRowModel extends MachineSupportedTest {
  /** The test's current per-test default machine key, or null. */
  currentDefaultMachineKey: string | null;
  /** Display name of the machine the default currently points to (if elsewhere). */
  currentDefaultMachineLabel: string | null;
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}
  >
    {children}
  </Typography>
);

export const MachineDefaultTestsDrawer: React.FC<MachineDefaultTestsDrawerProps> = ({
  open,
  machine,
  machines,
  onClose,
  onConfirm,
  onToast,
}) => {
  const theme = useTheme();

  /**
   * Resolve a machine key to its "Code" label (case-insensitive — SQL Server
   * upper-cases uniqueidentifier while client keys are lower-cased). Returns
   * null when the key doesn't resolve (e.g. a deactivated machine), so callers
   * can fall back to generic wording.
   */
  const resolveMachineLabel = useCallback(
    (key: string | null | undefined): string | null => {
      if (!key) return null;
      const match = machines.find((m) => eq(m.MachineKey, key));
      return match?.Code ?? null;
    },
    [machines],
  );
  const [rows, setRows] = useState<TestRowModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Test keys with an in-flight write (disables their Switch). */
  const [pending, setPending] = useState<Set<string>>(new Set());

  const machineKey = machine?.MachineKey ?? null;

  const load = useCallback(async () => {
    if (!machineKey) return;
    setLoading(true);
    setError(null);
    try {
      // Supported tests for this machine + full catalog (for "default elsewhere").
      const [supported, catalog] = await Promise.all([
        fetchMachineSupportedTests(machineKey),
        getTestCatalogItems(),
      ]);
      // Map TestCatalogItemKey (lower) → its current DefaultMachineKey.
      const defaultByTest = new Map<string, string | null>();
      for (const item of catalog) {
        defaultByTest.set(item.TestCatalogItemKey.toLowerCase(), item.DefaultMachineKey);
      }
      const merged: TestRowModel[] = supported.map((t) => {
        const currentDefault = defaultByTest.get(t.TestCatalogItemKey.toLowerCase()) ?? null;
        return {
          ...t,
          currentDefaultMachineKey: currentDefault,
          // Resolve to the machine's Code; null falls back to generic wording.
          currentDefaultMachineLabel: resolveMachineLabel(currentDefault),
        };
      });
      setRows(merged);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load supported tests';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [machineKey, resolveMachineLabel]);

  // (Re)load whenever the drawer opens for a (new) machine.
  useEffect(() => {
    if (open && machineKey) {
      load();
    } else if (!open) {
      // Clear stale rows so a fast reopen doesn't flash the previous machine.
      setRows([]);
      setError(null);
    }
  }, [open, machineKey, load]);

  const setRowPending = (testKey: string, on: boolean) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (on) next.add(testKey.toLowerCase());
      else next.delete(testKey.toLowerCase());
      return next;
    });
  };

  /** Perform the write (set or clear this machine as the test's default). */
  const writeDefault = useCallback(
    async (row: TestRowModel, makeDefault: boolean) => {
      if (!machineKey) return;
      setRowPending(row.TestCatalogItemKey, true);
      try {
        await updateTestCatalogItem(row.TestCatalogItemKey, {
          DefaultMachineKey: makeDefault ? machineKey : null,
        });
        // Optimistic local update: this machine becomes (or stops being) default.
        setRows((prev) =>
          prev.map((r) =>
            eq(r.TestCatalogItemKey, row.TestCatalogItemKey)
              ? {
                  ...r,
                  CurrentlyDefault: makeDefault,
                  currentDefaultMachineKey: makeDefault ? machineKey : null,
                  currentDefaultMachineLabel: null,
                }
              : r,
          ),
        );
        onToast(
          makeDefault
            ? `"${row.TestName}" now defaults to ${machine?.Code ?? 'this machine'}.`
            : `Cleared default machine for "${row.TestName}".`,
          'success',
        );
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to update default machine';
        onToast(msg, 'error');
        // Refetch to resync after a failed write.
        load();
      } finally {
        setRowPending(row.TestCatalogItemKey, false);
      }
    },
    [machineKey, machine?.Code, onToast, load],
  );

  /** Switch handler: confirm re-pointing when toggling ON over a different default. */
  const handleToggle = (row: TestRowModel, checked: boolean) => {
    if (!checked) {
      // Toggling OFF — clear the default (it points here, so no confirm needed).
      writeDefault(row, false);
      return;
    }
    const pointsElsewhere =
      row.currentDefaultMachineKey != null && !eq(row.currentDefaultMachineKey, machineKey);
    if (pointsElsewhere) {
      const currentLabel =
        resolveMachineLabel(row.currentDefaultMachineKey) ?? 'another machine';
      onConfirm({
        open: true,
        title: 'Change default machine?',
        message:
          `"${row.TestName}" currently defaults to ${currentLabel}. ` +
          `Make ${machine?.Code ?? 'this machine'} the default instead?`,
        confirmLabel: 'Set as default',
        confirmColor: 'primary',
        onConfirm: () => writeDefault(row, true),
      });
      return;
    }
    writeDefault(row, true);
  };

  const headerTitle = machine ? `${machine.Code} — ${machine.Name}` : 'Machine';

  const emptyState = !loading && !error && rows.length === 0;

  const defaultCount = useMemo(
    () => rows.filter((r) => r.CurrentlyDefault).length,
    [rows],
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 420, maxWidth: '94vw' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, pb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25 }}>
              {headerTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Tests this machine can run. Toggle a test to make this machine its default.
            </Typography>
            {!loading && rows.length > 0 && (
              <Chip
                size="small"
                label={`${defaultCount} default${defaultCount === 1 ? '' : 's'} on this machine`}
                sx={{ mt: 0.75, height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close tests panel">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {emptyState && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                This machine supports no active tests.
              </Typography>
            </Box>
          )}

          {!loading && rows.length > 0 && (
            <Stack divider={<Divider flexItem />} spacing={0}>
              {rows.map((row) => {
                const isPending = pending.has(row.TestCatalogItemKey.toLowerCase());
                const pointsElsewhere =
                  !row.CurrentlyDefault &&
                  row.currentDefaultMachineKey != null &&
                  !eq(row.currentDefaultMachineKey, machineKey);
                return (
                  <Box
                    key={row.TestCatalogItemKey}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1.25,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {row.TestName}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.25 }}
                      >
                        {row.AstmCode && (
                          <Typography variant="caption" color="text.secondary">
                            ASTM {row.AstmCode}
                          </Typography>
                        )}
                        {row.Category && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={row.Category}
                            sx={{ height: 18, fontSize: '0.6rem' }}
                          />
                        )}
                        {pointsElsewhere && (
                          <Tooltip
                            title={
                              row.currentDefaultMachineLabel
                                ? `This test currently defaults to ${row.currentDefaultMachineLabel}.`
                                : "This test's default machine is set on a different machine."
                            }
                          >
                            <Chip
                              size="small"
                              label={
                                row.currentDefaultMachineLabel
                                  ? `Default: ${row.currentDefaultMachineLabel}`
                                  : 'Default set elsewhere'
                              }
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                bgcolor: theme.schedule.noActivity,
                                color: theme.palette.getContrastText(theme.schedule.noActivity),
                              }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FieldLabel>Default</FieldLabel>
                      <Tooltip title="Default for this test">
                        <span>
                          <Switch
                            size="small"
                            checked={row.CurrentlyDefault}
                            disabled={isPending}
                            onChange={(e) => handleToggle(row, e.target.checked)}
                            inputProps={{ 'aria-label': `Default for ${row.TestName}` }}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Divider />

        {/* Footer */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
